import { CreativeBrief, CampaignConcept, VariationStrength } from "../types";
import { safeStorage } from "./storage";

/**
 * PURE CLIENT-SIDE GEMINI SERVICE WRAPPER
 * 
 * To prevent bringing heavy server-only Node modules (like @google/genai) into the 
 * client-side bundle, this file contains only HTTP requests that proxy calls 
 * to the backend Express server. This guarantees 100% browser-side compatibility 
 * and solves startup compilation or runtime errors.
 */

const getHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (typeof window !== 'undefined') {
    const localKey = safeStorage.getItem('STUDIO_USER_API_KEY');
    if (localKey) {
      headers['x-api-key'] = localKey;
    }
  }
  return headers;
};

export const refineBriefText = async (text: string): Promise<string> => {
  const response = await fetch('/api/refine-brief', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ text }),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Server error: ${response.status}`);
  }
  const data = await response.json();
  return data.text;
};

export const generateCampaignConcepts = async (
  brief: CreativeBrief
): Promise<CampaignConcept[]> => {
  const response = await fetch('/api/generate-concepts', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ brief }),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Server error: ${response.status}`);
  }
  const data = await response.json();
  return data.concepts;
};

export const regenerateSingleThemeConcept = async (
  brief: CreativeBrief,
  themeIndex: number
): Promise<CampaignConcept> => {
  const response = await fetch('/api/regenerate-concept', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ brief, themeIndex }),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Server error: ${response.status}`);
  }
  const data = await response.json();
  return data.concept;
};

export const generateTwinImage = async (
  brief: CreativeBrief,
  outfitImage: string | null,
  conceptPrompt: string,
  variationSource?: string,
  strength: VariationStrength = 'medium',
  isPro: boolean = false,
  onStatusUpdate?: (msg: string) => void
): Promise<string> => {
  if (onStatusUpdate) {
    onStatusUpdate("Contacting AI Twin Studio server...");
  }
  
  const response = await fetch('/api/generate-twin-image', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ brief, outfitImage, conceptPrompt, variationSource, strength, isPro }),
  });
  
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Server error: ${response.status}`);
  }
  const data = await response.json();
  return data.image;
};
